import { Role } from '@anecdotes/api-contracts';
import { FeatureFlagsService } from '@anecdotes/feature-flags';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of, switchMap, combineLatest, take } from 'rxjs';
import { staticRoles } from '../../constants';
import { CalculatedRole } from '../../models';
import { RoleEnum } from '@anecdotes/auth-core';

const DEFAULT_ORDER: string[] = [
  RoleEnum.Admin,
  RoleEnum.Manager,
  RoleEnum.Collaborator,
  RoleEnum.Contributor,
  RoleEnum.Viewer,
  RoleEnum.It,
  RoleEnum.TrustCenterAccessManager,
  RoleEnum.Auditor,
];

@Injectable()
export class RoleApiService {
  private readonly baseUrl: string;

  constructor(private httpService: HttpClient, private featureFlagsService: FeatureFlagsService) {
    this.baseUrl = window['__AnPlatformConfiguration__'].identityApi.baseUrl;
  }

  getRoles(): Observable<Role[]> {
    return combineLatest([
      this.featureFlagsService.isEnabled('@roles-page'),
      this.featureFlagsService.isEnabled('@trust-center-tab'),
      this.featureFlagsService.isEnabled('@contributor'),
    ]).pipe(
      take(1),
      switchMap(([hasAccess, hasTrustCenter, isContributor]) => {
        if (hasAccess || isContributor) {
          let staticCalculatedRoles = staticRoles.map((role) => ({ ...role, is_static: true } as CalculatedRole));

          if (!hasTrustCenter) {
            staticCalculatedRoles = staticCalculatedRoles.filter((role) => role.key !== RoleEnum.TrustCenterAccessManager);
          }

          // TODO: Static roles (admin, manager, etc) should be returned from BE
          // but currently it's hardcoded in FE until migrated
          return this.httpService.get<Role[]>(`${this.baseUrl}/roles`).pipe(
            map((rolesFromServer) =>
              [...staticCalculatedRoles, ...rolesFromServer].sort((role1, role2) => {
                const indexA = DEFAULT_ORDER.indexOf(role1.key);
                const indexB = DEFAULT_ORDER.indexOf(role2.key);

                if (indexA === -1 && indexB === -1) {
                  return 0;
                }
                if (indexA === -1) {
                  return 1;
                }
                if (indexB === -1) {
                  return -1;
                }

                return indexA - indexB;
              }),
            ),
          );
        }

        // TODO: If no access to roles-page, return only static roles
        return of(staticRoles);
      }),
    );
  }

  createRole(role: Role): Observable<Role> {
    return this.httpService.post<Role>(`${this.baseUrl}/roles`, role);
  }

  updateRole(role: Role): Observable<Role> {
    return this.httpService.put<Role>(`${this.baseUrl}/roles`, role);
  }

  deleteRole(roleId: string): Observable<void> {
    return this.httpService.delete<void>(`${this.baseUrl}/roles?role_id=${roleId}`);
  }
}
